import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { AdminModeProvider } from './contexts/AdminModeContext';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { HolidayThemeProvider } from './contexts/HolidayThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MobileBottomNav, { MOBILE_NAV_SPACER } from './components/MobileBottomNav';
import CookieConsent from './components/CookieConsent';
import HolidaySnowfall from './components/holiday/HolidaySnowfall';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ScrollToTop from './components/ScrollToTop';
import { analyticsAPI } from './services/api';
import { getVisitorId } from './utils/visitor';

// Records one visit per browser session. The backend further deduplicates per
// visitor per day, so this is safe to call on every load.
const VisitTracker: React.FC = () => {
  useEffect(() => {
    try {
      if (sessionStorage.getItem('visit_recorded')) return;
      sessionStorage.setItem('visit_recorded', '1');
    } catch {
      // ignore storage errors and still attempt to record
    }
    analyticsAPI.recordVisit(getVisitorId()).catch(() => {});
  }, []);
  return null;
};

const Home = React.lazy(() => import('./pages/Home'));
const Products = React.lazy(() => import('./pages/Products'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Checkout = React.lazy(() => import('./pages/Checkout'));
const PaymentResult = React.lazy(() => import('./pages/PaymentResult'));
const Profile = React.lazy(() => import('./pages/Profile'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const ShippingInfo = React.lazy(() => import('./pages/ShippingInfo'));
const DataDeletion = React.lazy(() => import('./pages/DataDeletion'));

const Favourites = React.lazy(() => import('./pages/Favourites'));
const Orders = React.lazy(() => import('./pages/Orders'));
const AdminLayout = React.lazy(() => import('./pages/admin/AdminLayout'));
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const AdminProducts = React.lazy(() => import('./pages/admin/AdminProducts'));
const AdminCategories = React.lazy(() => import('./pages/admin/AdminCategories'));
const AdminUsers = React.lazy(() => import('./pages/admin/AdminUsers'));
const AdminFinances = React.lazy(() => import('./pages/admin/AdminFinances'));
const AdminOrders = React.lazy(() => import('./pages/admin/AdminOrders'));
const AdminSettings = React.lazy(() => import('./pages/admin/AdminSettings'));
const AdminLegal = React.lazy(() => import('./pages/admin/AdminLegal'));
const AdminMaintenance = React.lazy(() => import('./pages/admin/AdminMaintenance'));
const AdminImages = React.lazy(() => import('./pages/admin/AdminImages'));
const AdminMostViewed = React.lazy(() => import('./pages/admin/AdminMostViewed'));
const AdminTopBought = React.lazy(() => import('./pages/admin/AdminTopBought'));

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="loading-spinner h-8 w-8"></div>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <VisitTracker />
      <LanguageProvider>
        <HolidayThemeProvider>
        <AuthProvider>
          <AdminModeProvider>
          <CartProvider>
            <WishlistProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin routes - separate layout, no Navbar/Footer */}
                <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="analytics/most-viewed" element={<AdminMostViewed />} />
                  <Route path="analytics/top-bought" element={<AdminTopBought />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="finances" element={<AdminFinances />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="legal" element={<AdminLegal />} />
                  <Route path="images" element={<AdminImages />} />
                  <Route path="maintenance" element={<AdminMaintenance />} />
                </Route>

                {/* Store routes - with Navbar and Footer */}
                <Route path="*" element={<StoreLayout />} />
              </Routes>
            </Suspense>
            <Toaster
              position="bottom-right"
              gutter={12}
              containerStyle={{ right: 24 }}
              // The library sets `bottom` inline, so overriding it to clear the
              // mobile tab bar needs `!important`.
              containerClassName="!bottom-[calc(4.5rem_+_env(safe-area-inset-bottom))] lg:!bottom-6"
              toastOptions={{ style: { background: 'transparent', boxShadow: 'none', padding: 0 } }}
            />
            </WishlistProvider>
          </CartProvider>
          </AdminModeProvider>
        </AuthProvider>
        </HolidayThemeProvider>
      </LanguageProvider>
    </Router>
  );
}

function StoreLayout() {
  return (
    <div className={`min-h-screen bg-neutral-50 flex flex-col ${MOBILE_NAV_SPACER}`}>
      <HolidaySnowfall />
      <Navbar />
      <main className="p-0 flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/favourites" element={<ProtectedRoute><Favourites /></ProtectedRoute>} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/payment/result" element={<ProtectedRoute><PaymentResult /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/shipping" element={<ShippingInfo />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
        </Routes>
      </main>
      <Footer />
      <MobileBottomNav />
      <CookieConsent />
    </div>
  );
}

export default App;
